import { useEffect, useRef } from "react";
import type { RefObject } from "react";

import { pushEscapeLayer } from "./escapeLayers";

/** Everything the browser lets you reach with Tab inside a modal layer. */
export const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Whether an element can actually take the focus: not hidden, not inert, not undisplayed. */
function isShown(element: HTMLElement): boolean {
  if (element.closest("[hidden], [inert]") !== null) return false;
  const style = getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden";
}

/** The reachable controls of `container`, in tab order; hidden ones are not stops (verifier m2). */
export function focusableIn(container: HTMLElement | null): HTMLElement[] {
  return container === null ? [] : [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(isShown);
}

/**
 * The focus contract every modal layer shares (ConfirmDialog, Dialog, Drawer).
 *
 * On open it remembers what had the focus and moves it to `initialFocus(container)`, else to the
 * first control. Escape calls `onEscape` when this layer is the topmost one (`escapeLayers`) and claims the key, so no page shortcut
 * also acts on it (F-UIX-017). Tab and Shift+Tab wrap inside the container: "trapped" means Tab never
 * walks into the page the layer covers. On close the focus goes back where it was.
 *
 * The handler is on the document, not on the layer: Escape must work wherever the focus is, and a
 * `keydown` on a non-interactive element is the pattern `jsx-a11y` warns about. `onEscape` lives in
 * a ref, so a parent that re-creates it every render does not tear the trap down mid-typing.
 */
export function useModalFocus(
  open: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onEscape: () => void,
  initialFocus?: (container: HTMLElement) => HTMLElement | null | undefined,
): void {
  const escapeRef = useRef(onEscape);
  const initialRef = useRef(initialFocus);
  useEffect(() => {
    escapeRef.current = onEscape;
    initialRef.current = initialFocus;
  }, [onEscape, initialFocus]);

  useEffect(() => {
    if (!open) return undefined;

    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const container = containerRef.current;
    const initial = container === null ? undefined : initialRef.current?.(container);
    (initial ?? focusableIn(container)[0])?.focus();

    const removeLayer = pushEscapeLayer(() => {
      escapeRef.current();
    });

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Tab") return;

      const stops = focusableIn(containerRef.current);
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (first === undefined || last === undefined) return;
      // A focus that left the layer (a press on its plain text puts it on the body) comes back in.
      const inside = containerRef.current?.contains(document.activeElement) === true;
      if (!inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      removeLayer();
      restoreTo?.focus();
    };
  }, [open, containerRef]);
}

/** `data-autofocus`, else the first control that is not the layer's own close button. */
export function preferredFocus(container: HTMLElement): HTMLElement | null | undefined {
  return (
    container.querySelector<HTMLElement>("[data-autofocus]") ??
    focusableIn(container).find((element) => element.closest("[data-close]") === null)
  );
}
